import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { LogoutIcon } from "@/components/ui/logout-icon";
import { useClerkAuth } from "@/hooks/useClerkAuth";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { PAYWALL_HEADER } from "../../constants/subscriptionPlans";
import { SubscriptionPlanPicker } from "./SubscriptionPlanPicker";

interface SubscriptionRequiredModalProps {
  isOpen: boolean;
}

const brandTextClassName = "text-[color:var(--brand-tropical-surf)]";

function PaywallSignOutButton() {
  const { signOut } = useClerkAuth();
  const [signingOut, setSigningOut] = React.useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      window.location.assign("/auth/signin");
    } catch (e) {
      console.error("Sign out error:", e);
      setSigningOut(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline-red"
      size="sm"
      data-paywall-logout=""
      className="shrink-0 gap-2"
      disabled={signingOut}
      onClick={() => void handleSignOut()}
    >
      <LogoutIcon size={16} className="text-current" aria-hidden />
      {signingOut ? "Signing out…" : "Log out"}
    </Button>
  );
}

/**
 * Paywall plan picker — mirrors the marketing pricing cards. Not dismissible: access is gated
 * until checkout completes.
 */
export default function SubscriptionRequiredModal({
  isOpen,
}: SubscriptionRequiredModalProps) {
  return (
    <Dialog open={isOpen}>
      <DialogContent
        data-paywall-plan-picker=""
        showCloseButton={false}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        className="flex max-h-[92vh] flex-col gap-6 overflow-y-auto p-6 sm:max-w-5xl sm:p-8 lg:max-w-6xl"
      >
        <DialogTitle className="sr-only">
          {PAYWALL_HEADER.titleLead} {PAYWALL_HEADER.titleAccent}
        </DialogTitle>

        <SubscriptionPlanPicker />

        <div className="mt-3 flex w-full flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
          <p className="text-sm text-muted-foreground">
            After you subscribe, manage payment methods, invoices, and cancellation
            in{" "}
            <RouterLink
              data-paywall-billing-link=""
              to="/profile/billing"
              className={cn(
                "inline-flex items-center gap-1 font-medium",
                brandTextClassName
              )}
            >
              Account settings
              <ChevronRight className="size-3.5 shrink-0" aria-hidden />
              Billing
            </RouterLink>
            .
          </p>

          <PaywallSignOutButton />
        </div>
      </DialogContent>
    </Dialog>
  );
}
